/**
 * COMPAT SHIM — temporary, deleted with `src/compat`. See its README.
 *
 * ## The failure this prevents
 *
 * On affected STForestKit hosts (≤ forest-ios 5.23.0, older WebKit) the injected
 * console-bridge script replaces `console.log/debug/info/warn/error` with wrappers that
 * throw `ReferenceError` on every call: Annex B hoists the script's function
 * *declarations* out of the once-guard's `if` block but leaves its `const` bindings
 * behind, and reached through the hoisted binding those functions run with the global
 * environment, so `originalConsoleLogger` / `pendingNativeLogs` are unresolvable.
 *
 * From that moment **every `console.*` call in the document throws** — the host's own
 * scripts, this library, and the host app alike. Three consequences, all observed on a
 * physical iPhone 8 against the deployed staging site:
 *
 *   1. The RPC script dies on its first statement (a `console.log`) before defining
 *      `window.Bifrost`, so the native bridge is never created.
 *   2. This library throws from its own logging (9 call sites, and the host app enables
 *      debug logging), which surfaces as a `ReferenceError` rejecting `initialize()`.
 *   3. The host app's `catch` reports that `ReferenceError` to the user verbatim.
 *
 * (3) is why a global error listener cannot help: the throw is caught by application
 * code, so no `error` event is ever dispatched.
 *
 * ## The approach
 *
 * Install `console.log/debug/info/warn/error` as accessors at module load, before the
 * host injects. The getter always returns a guarded function; the setter records what
 * the host assigns instead of replacing the guard. The guard calls whatever the host
 * installed and, if that throws, falls back to the pristine method captured at load.
 *
 * So nothing in the document can ever throw from a `console` call, while a *healthy*
 * host's wrapper still runs and its console forwarding keeps working. That makes this
 * safe without version-gating: on a fixed host the guard is a pass-through.
 *
 * It also fixes the bridge at the source — with `console.log` no longer throwing, the
 * RPC script runs to completion on its **first** injection, so the readiness watchdog
 * never has to retry. `StForestKitOnceGuardRecovery` remains as a safety net for a host
 * whose `console` could not be guarded, or which fails for some other reason.
 */

/** Console methods the affected host replaces, and therefore the ones that can throw. */
const GUARDED_METHODS = ["log", "debug", "info", "warn", "error"] as const;
type GuardedMethod = (typeof GUARDED_METHODS)[number];
type ConsoleMethod = (...args: unknown[]) => void;

/**
 * Pristine console methods, captured at module evaluation.
 *
 * Only as good as import ordering: the host injects on DOM-ready, so a bundle evaluated
 * before then — the normal case, and guaranteed for a dependency of the page's own
 * bundle — captures working functions. A bundle evaluated later would capture the
 * host's throwing wrappers, which is why the guard treats a throwing fallback as
 * "nothing more to try" rather than assuming the snapshot is good.
 */
const pristine: Partial<Record<GuardedMethod, ConsoleMethod>> = (() => {
  const snapshot: Partial<Record<GuardedMethod, ConsoleMethod>> = {};
  if (typeof console === "undefined") {
    return snapshot;
  }
  for (const name of GUARDED_METHODS) {
    const method = (console as unknown as Record<string, unknown>)[name];
    if (typeof method === "function") {
      snapshot[name] = (method as ConsoleMethod).bind(console) as ConsoleMethod;
    }
  }
  return snapshot;
})();

/** The pristine snapshot, for the recovery shim's own repair path. */
export function pristineConsoleMethods(): Partial<
  Record<GuardedMethod, ConsoleMethod>
> {
  return { ...pristine };
}

let installed = false;
let fallbackCount = 0;

/** How many console calls fell back to the pristine method. Diagnostics and tests. */
export function stForestKitConsoleFallbackCount(): number {
  return fallbackCount;
}

/**
 * Make every `console.*` call non-throwing, permanently.
 *
 * Idempotent; a no-op when there is no `console`, and per-method tolerant of a
 * `console` whose properties cannot be redefined.
 */
export function installStForestKitConsoleGuard(): void {
  if (installed || typeof console === "undefined") {
    return;
  }
  installed = true;

  for (const name of GUARDED_METHODS) {
    const fallback = pristine[name];
    if (!fallback) {
      continue;
    }

    // What the host most recently assigned. Starts as the pristine method so the guard
    // is a faithful pass-through until the host replaces it.
    let current: ConsoleMethod = fallback;

    // Re-entrancy latch. Some code does `console.log = console.log.bind(console)`,
    // which would store a function that calls the guard again; without this the first
    // call would recurse until the stack blew. Depth is capped at one.
    let inFlight = false;

    const guarded: ConsoleMethod = (...args: unknown[]) => {
      if (inFlight) {
        try {
          fallback(...args);
        } catch {
          /* nothing left to try */
        }
        return;
      }
      inFlight = true;
      try {
        current(...args);
      } catch {
        fallbackCount += 1;
        try {
          fallback(...args);
        } catch {
          /* nothing left to try */
        }
      } finally {
        inFlight = false;
      }
    };

    try {
      Object.defineProperty(console, name, {
        configurable: true,
        enumerable: true,
        get: () => guarded,
        set: (value: unknown) => {
          if (typeof value === "function") {
            current = value as ConsoleMethod;
          }
        },
      });
    } catch {
      // A `console` that refuses redefinition cannot be guarded. Leave it alone; the
      // recovery shim's repair path is the remaining line of defence.
    }
  }
}

/** Test seam: forget the installed state and the fallback tally. */
export function resetStForestKitConsoleGuardForTests(): void {
  installed = false;
  fallbackCount = 0;
}
